import { Link } from '@tanstack/react-router'
import { ArrowRight, Check, Lock, Minus, Rocket, X } from 'lucide-react'
import { notifyError } from '@/components/notify'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Meter } from '@/components/ui/progress'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useDismissOnboarding } from '@/features/onboarding/api'
import { cn } from '@/lib/utils'
import type { Onboarding } from '@/types/api'

export function OnboardingChecklist({ onboarding }: { onboarding: Onboarding }) {
  const dismiss = useDismissOnboarding()
  const steps = onboarding.steps.filter((s) => s.key !== 'account')
  const nextStep = steps.find((s) => s.available && s.status === 'pending')
  return (
    <Card className="relative overflow-hidden">
      <div className="pointer-events-none absolute -top-20 -right-20 size-56 rounded-full bg-brand-gradient opacity-[0.10] blur-3xl" aria-hidden />
      <div className="relative flex items-start gap-4 p-5 pb-4">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-brand-gradient text-white shadow-glow">
          <Rocket className="size-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-[15px] font-semibold">Finish setting up Signetix</h2>
          <p className="text-[13px] text-muted-foreground">
            {nextStep ? <>Next up: <span className="font-medium text-foreground">{nextStep.title}</span></> : 'You’ve completed every available step.'}
          </p>
          <div className="mt-3 flex items-center gap-3">
            <Meter value={onboarding.progress / 100} tone="brand" className="max-w-xs" label="Onboarding progress" />
            <span className="text-xs font-medium text-muted-foreground tabular">{Math.round(onboarding.progress)}%</span>
          </div>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label="Dismiss checklist" loading={dismiss.isPending} onClick={() => dismiss.mutate(undefined, { onError: (e) => notifyError(e) })}>
              {!dismiss.isPending && <X />}
            </Button>
          </TooltipTrigger>
          <TooltipContent>Hide checklist</TooltipContent>
        </Tooltip>
      </div>
      <ol className="relative -mr-px -mb-px grid border-t sm:grid-cols-2 xl:grid-cols-3">
        {steps.map((step) => {
          const done = step.status === 'complete'
          const skipped = step.status === 'skipped'
          return (
            <li key={step.key} className={cn('flex items-center gap-2.5 border-r border-b bg-card px-5 py-2.5 text-[13px]', !step.available && 'text-muted-foreground')}>
              <span
                className={cn(
                  'flex size-5 shrink-0 items-center justify-center rounded-full border',
                  done && 'border-transparent bg-success text-white',
                  skipped && 'border-dashed',
                )}
              >
                {done ? <Check className="size-3" strokeWidth={3} /> : skipped ? <Minus className="size-3" /> : !step.available ? <Lock className="size-2.5" /> : null}
              </span>
              <span className={cn('truncate', done && 'text-muted-foreground line-through decoration-muted-foreground/40')}>{step.title}</span>
              {!step.available && (
                <Badge variant="outline" className="ml-auto border-dashed text-[10px] font-normal">
                  Soon
                </Badge>
              )}
            </li>
          )
        })}
      </ol>
      {nextStep && (
        <div className="flex justify-end border-t px-5 py-3">
          <Button size="sm" asChild>
            <Link to="/onboarding">
              Continue setup <ArrowRight />
            </Link>
          </Button>
        </div>
      )}
    </Card>
  )
}
